import React from 'react';
import * as Sentry from '@sentry/react';
import { AlertTriangle, RotateCw } from 'lucide-react';
import logoWhite from '../../assets/images/flexcoa-logo-condensed.png';
import logoGreen from '../../assets/images/flexcoa-logo-condensed-green.png';

// Full-page state shown when the app crashes. It sits outside the router and
// the providers, so it uses a plain link and a page reload.
const CrashScreen = () => (
  <div className="flex min-h-screen flex-col bg-canvas">
    <div className="px-6 pt-6 sm:px-8">
      <img src={logoGreen} alt="flexCOA" className="h-8 w-auto dark:hidden" />
      <img src={logoWhite} alt="" aria-hidden="true" className="hidden h-8 w-auto dark:block" />
    </div>

    <main className="flex flex-1 items-center justify-center px-4 pb-24 pt-10">
      <div className="card w-full max-w-md px-8 py-10 text-center">
        <AlertTriangle className="mx-auto h-6 w-6 text-gray-400 dark:text-gray-500" strokeWidth={1.75} aria-hidden="true" />
        <h1 className="mt-4 text-xl font-semibold tracking-tight text-gray-900 dark:text-gray-50">Something went wrong.</h1>
        <p className="mx-auto mt-2 max-w-sm text-[13.5px] leading-relaxed text-gray-500 dark:text-gray-400">
          An unexpected error stopped this page from loading. Try again, or go back to your studies.
        </p>
        <div className="mt-7 flex flex-col-reverse justify-center gap-2 sm:flex-row">
          <a href="/studies" className="btn btn-secondary">
            Go to studies
          </a>
          <button type="button" onClick={() => window.location.reload()} className="btn btn-primary">
            <RotateCw className="h-4 w-4" aria-hidden="true" />
            Try again
          </button>
        </div>
      </div>
    </main>
  </div>
);

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.log('ErrorBoundary caught an error:', error, errorInfo);
    Sentry.captureException(error, {
      contexts: { react: { componentStack: errorInfo.componentStack } },
    });
    this.logError('frontend', error.message, errorInfo.componentStack);
  }

  logError(type, message, stack) {
    console.log('Logging error:', type, message, stack);
    const errors = JSON.parse(localStorage.getItem('appErrors') || '{"frontend":[],"backend":[]}');
    errors[type].push({
      id: Date.now(),
      message,
      stack,
      timestamp: new Date().toISOString(),
    });
    localStorage.setItem('appErrors', JSON.stringify(errors));
    console.log('Updated localStorage:', localStorage.getItem('appErrors'));
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback || <CrashScreen />;
    }

    return this.props.children;
  }
}

export default ErrorBoundary;